import React, { useMemo } from 'react';
import useSetExport from '@components/export/useSetExport';
import { useSelector } from 'react-redux';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { isMetricFilterAppliedSelector } from '@store/trends/base/selectors';
import { getMetricMetaByKey, getPercentageMetric } from '@utils/metrics/metricHelpers';
import { getMetricFilterMetric, getMetricFilterPctMetric } from '../dimension-table/utils';
import { buildIntervalText } from '@components/export/parsers';
import { MF_LABEL } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import {
  chartIntervalSelector,
  tableCompareIntervalSelector,
  tableCompareEnabledSelector,
  noBrushSelector,
} from '@store/trends/birdChart/selectors';
import { getCompareIntervalLabel } from '@src/components/dimension-panel-hz/utils';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import { useAtomValue } from 'jotai';

function useSetDimensionExport({ dimension, metrics, data = [], totals = {}, showNAForMetric }) {
  const dimensions = useSelector(dimensionsListSelectorKeyValue);
  const isMetricFilterApplied = useSelector(isMetricFilterAppliedSelector);
  const interval = useSelector(chartIntervalSelector);

  const isTableCompareEnabled = useSelector(tableCompareEnabledSelector);
  const compareInterval = useSelector(tableCompareIntervalSelector);
  const dodEnabled = useAtomValue(dodAtom);
  const isSliceAvailable = !useSelector(noBrushSelector);

  const headers = useMemo(() => {
    const headers: any = [
      {
        key: 'name',
        title: '',
        type: 'dimension',
      },
    ];

    try {
      metrics.forEach((metric) => {
        const metricInfo = getMetricMetaByKey({ metricKey: metric });
        const pctMetric = getPercentageMetric(metric);
        const type = !!showNAForMetric && showNAForMetric(metric) ? 'notApplicable' : null;

        // base metric
        headers.push({ key: metric, title: metricInfo.short, type });

        // metric filter metric
        if (isMetricFilterApplied) {
          headers.push({
            key: getMetricFilterMetric(metric),
            title: `[${MF_LABEL}] ` + metricInfo.short,
            type,
          });
        }

        if (isTableCompareEnabled) {
          const compareLabel = getCompareIntervalLabel({
            isSliceAvailable,
            dodEnabled,
            compareInterval,
          });
          headers.push({
            key: getMetricFilterMetric(metric),
            title: `${compareLabel}`,
            type,
          });
        }

        // percentage metric
        if (metric !== pctMetric) {
          headers.push({ key: pctMetric, title: metricInfo.short + ' (%)', type });

          // percentage metric filter metric
          if (isMetricFilterApplied) {
            headers.push({
              key: getMetricFilterMetric(pctMetric),
              title: `[${MF_LABEL}] ` + metricInfo.short + ' (%)',
              type,
            });
          }
        }

        // MF relative % metric
        if (isMetricFilterApplied) {
          headers.push({
            key: getMetricFilterPctMetric(metric),
            title: `[${MF_LABEL}%] ` + metricInfo.short + ' (%)',
            type,
          });
        }
      });
    } catch (err) {
      // pass
    }

    return headers;
  }, [metrics, data]); // applying MF doesn't change metrics list, data dep will refresh headers

  useSetExport({
    pageKey: 'trends',
    sectionKey: 'dimensions-panel',
    widgetKey: `dimension-table::${dimension}`,
    headers,
    data: [totals].concat(data),
    widgetInfo: {
      title: `Dimension: ${dimensions[dimension]} (${buildIntervalText(interval)})`,
    },
  });
}

export default useSetDimensionExport;
